import * as Mui from "@mui/material";
import { ClockPickerView, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import i18n from "../../../../config/dictionary/i18n";
import SectionHeader from "../../../../util/component/SectionHeader";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useToggle from "../../hooks/useToggle";
import { IVehicleAvailabilityState } from "../../page/VehicleAvailabilityPage";
import OperationTypes from "../../types";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import EnumChangeName from "../common/EnumChangeName";
import VehicleAvailabilityBlock from "./VehicleAvailabilityBlock";
import Dialogs from "./dialogs";
import VehicleAvailabilityService, { EnumReasonCodeState, EnumStatusReasons, IUnavailabilityReasonRequest } from "./service/VehicleAvailabilityService";

export interface FormInputs  {
    status: any;
    startDate: string | null;
    endDate?: string | null;
}

const VehicleAvailabilityStatus = () => {
    const location = useLocation();
    const vehicleData: IVehicleAvailabilityState = location.state as IVehicleAvailabilityState;

    const t = useOperationTranslation();
    const { locationType } = EnumChangeName();

    const [openConfirm, toggleConfirm] = useToggle();
    const [openChangeStatus, toggleChangeStatus] = useToggle();
    const [listStatus, setListStatus] = useState<AutoCompleteOption[]>([]);
    const [listStatusSpecial, setListStatusSpecial] = useState<AutoCompleteOption[]>([]);
    const [availabilityStatus, setAvailabilityStatus] = useState<null | OperationTypes.VehicleAvailability.Status>(null);
    const [listBlocks, setListBlocks] = useState<IUnavailabilityReasonRequest[]>([]);
	const [oriListBlocks, setOriListBlocks] = useState<IUnavailabilityReasonRequest[]>([]);
    const [enableConfirm, setEnableConfirm] = useState<boolean>(listBlocks.length != oriListBlocks.length);

    const getData = useCallback(() => {
        return VehicleAvailabilityService.get.unavailabilityByVehicle(vehicleData.tenant,vehicleData.id).then(
            response => {
                setAvailabilityStatus(response);
                setListBlocks(response?.lastUnavailabilityHistory?.unavailabilityReasons || []);
				setOriListBlocks(response?.lastUnavailabilityHistory?.unavailabilityReasons || []);
                return response;
            },
        );
    }, []);

    const getAvailabilityStatusReasons = () => {
        VehicleAvailabilityService.get.availabilityStatusReasons(vehicleData.tenant,EnumStatusReasons.NOT_AVAILABLE, false, vehicleData.plate).then(
            response => {
                setListStatus(response.map((stat: any) => ({
                    value: stat.code,
                    label: `${stat.code} - ${stat.descr}`,
                    disabled: !stat?.activeForVehicle || false,
                })))
            }
        );
    }
    
    useEffect(() => {
        getAvailabilityStatusReasons();

        VehicleAvailabilityService.get.availabilityStatusReasons(vehicleData.tenant,EnumStatusReasons.NOT_AVAILABLE, true, vehicleData.plate).then(
            response => {
                setListStatusSpecial(response.map((stat: any) => ({
                    value: stat.code,
                    label: `${stat.code} - ${stat.descr}`,
                    disabled: !stat?.activeForVehicle || false,
                })))
            }
        )


        getData();
    }, []);

    const form = useForm<FormInputs>(
        {
            defaultValues: {
                startDate: null,
                endDate: null 
            }
        }
    )
    
    const onSumbit = (inputValues:FormInputs) => {
        const newListBlocks = [...listBlocks];

        if(inputValues.status === null || inputValues.status === undefined) {
            return;
        }
        if(inputValues.startDate === null || inputValues.startDate === undefined) {
            return;
        }

        const newBlock: IUnavailabilityReasonRequest = {
            id: null,
            expectedStartUnavailabilityDatetime: inputValues.startDate,
            actualStartUnavailabilityDatetime: inputValues.startDate,
            expectedEndUnavailabilityDatetime: inputValues.endDate,
            statusReasonCode: inputValues.status,
        }

        const findExistingBlock = (element: IUnavailabilityReasonRequest) => {
            if (element.statusReasonCode === inputValues.status && element.reasonCodeState !== EnumReasonCodeState.CLOSED) {
                return true;
            }
            return false;
        }
        let blockIndexFound: number = newListBlocks.findIndex(findExistingBlock);
        if (blockIndexFound !== -1) {
            newListBlocks.splice(blockIndexFound, 1, newBlock);
        } else {
            newListBlocks.push(newBlock);
        }
        
        setListBlocks(newListBlocks);
        setEnableConfirm(true);
        form.reset();
    }
    
    const onRemove = (index: number) => {
        const newData = listBlocks.filter((element, newIndex) => {
            if(index !== newIndex) {
                return true;
            }else {
                return false;
            }
        });

        setListBlocks(newData);
        setEnableConfirm(newData.length != oriListBlocks.length);
    }

    const onConfirmBlocks = () => {
        getData().then(
            response => {
                if (listBlocks.length === 0) {
                    toggleChangeStatus();
                }
            }
        );
        
        getAvailabilityStatusReasons();

        VehicleAvailabilityService.get.unavailabilityByVehicle(vehicleData.tenant,vehicleData.id).then(
            response => {
                setOriListBlocks(response?.lastUnavailabilityHistory?.unavailabilityReasons || []);
                return response;
            },
        );
        setEnableConfirm(false);
    }

    const onChangeAvailabilityStatus = (status: OperationTypes.VehicleAvailability.Status) => {
        setAvailabilityStatus(status);
    }

    const disabledStartDates = (date: string) => {
	
        const status = form.watch("status");
    
        if (status == 'SOLD') {

            if(dayjs(date).isAfter(dayjs())) {
                return true;
            }
            return false;

        } else {

            if(dayjs(date).isBefore(dayjs().subtract(1, "day"))) {
                return true;
            }
            return false;

        }

    }
    const disabledStartHours = (time: number, type: ClockPickerView) => {
        const dateSelected = form.watch("startDate");

        if (form.watch("status") == 'SOLD') {


            if (dayjs(dateSelected).daysInMonth == dayjs().daysInMonth &&
                dayjs(dateSelected).month == dayjs().month &&
                dayjs(dateSelected).year == dayjs().year
            ) {

                if (type === "hours" && time > dayjs().get("hours")) {

                    return true;
                }
                if (type === "minutes" && time > dayjs().get("minutes")) {
                    return true;
                }
                if (type === "seconds" && time > dayjs().get("seconds")) {
                    return true;
                }
            } else if (dayjs(dateSelected).isBefore(dayjs())) {
                return false;
            }

        } else {

            if (dayjs(dateSelected).isAfter(dayjs())) {
                return false;
            }

            if (type === "hours" && time < dayjs().get("hours")) {
                return true;
            }
            if (type === "minutes" && time < dayjs().get("minutes")) {
                return true;
            }
            if (type === "seconds" && time < dayjs().get("seconds")) {
                return true;
            }
        }
        return false;
    }



    const disableEndHours = (time: number, type: ClockPickerView) => {
        const dateSelected = dayjs(form.watch("endDate"));
        const dateStart = dayjs(form.watch("startDate"));

        if (dateSelected.get("date") > dateStart.get("date")) {
            return false;
        }

        if (type === "hours" && time < dateStart.get("hours")) {
            return true;
        }
        if (type === "minutes" && time < dateStart.get("minutes") && dateSelected.get("hours") === dateStart.get("hours")) {
            return true;
        }
        if (type === "seconds" && time < dateStart.get("seconds")) {
            return true;
        }
        return false;
    }

    const availabilityDescr = availabilityStatus?.availabilityHistory?.availabilityDescr ? `| ${availabilityStatus.availabilityHistory.availabilityDescr}` : "";
    const chipLabel = `${availabilityStatus?.vehicleStatus} ${availabilityDescr}`;

    const referenceLocation = useMemo(() => {
        return `${locationType(vehicleData.referenceLocationType)} - ${vehicleData.referenceLocationDesc}`;
    }, [i18n.language]);

    return(
    <>    
        <Mui.Box paddingBottom="16px">
            <SectionHeader
                title={t("vehicleAvailability.status.titleBlue") + " " + t("vehicleAvailability.search.titleViolet")}
            />
            <Mui.Grid container spacing={{ md:2 }} p={{ md:2 }}>
                <Mui.Grid item md={6} xs={12}>
                    <Mui.Card>
                        <Mui.CardContent>
                            <Mui.Grid container spacing={2}>

                                <Mui.Grid item xs={5}>
                                    <Mui.Stack gap={2} alignItems="center" mb={2}>
                                        <Mui.Skeleton
                                            variant="rectangular"
                                            width="150px"
                                            height="150px"
                                        />

                                        <Mui.Typography variant="h4" mb="10px" textAlign={{ md: "left", xs: "center"}}>
                                            {vehicleData.modelType}
                                        </Mui.Typography>

                                        <Mui.Chip
                                            label={chipLabel}
                                            color={availabilityStatus?.vehicleStatus === EnumStatusReasons.AVAILABLE ? "info" : "warning"}
                                            onClick={availabilityStatus?.vehicleStatus === EnumStatusReasons.AVAILABLE ? toggleChangeStatus : undefined}
                                        />
                                    </Mui.Stack>
                                </Mui.Grid>
                                <Mui.Grid item xs={7}>
                                    <Mui.Stack gap={2} alignItems="left" mb={2}>
                                        <Mui.Box sx={{ display: "flex", gap:"10px"}} pt="20px">
                                            <Mui.Typography variant="body1">
                                                {t("vehicleAvailability.status.currentState.labels.plate")}
                                            </Mui.Typography>
                                            <Mui.Typography variant="h5">
                                                {vehicleData.plate}
                                            </Mui.Typography>
                                        </Mui.Box>

                                        <Mui.Box sx={{ gap:"10px" }} m="16px 0px">
                                            <Mui.Typography variant="body1">
                                                {t("vehicleAvailability.status.currentState.labels.referenceLocation")}
                                            </Mui.Typography>
                                            <Mui.Typography variant="h5">
                                                {referenceLocation !== 'undefined - undefined' ? referenceLocation : t("vehicleAvailability.status.currentState.labels.noReferenceLocation")}
                                            </Mui.Typography>
                                        </Mui.Box>

                                        <Mui.Box sx={{ gap:"10px" }} m="16px 0px">
                                            <Mui.Button
                                                fullWidth
                                                variant="contained"
                                                //style={{ color: "#d12c93", backgroundColor: "white"}}
                                                sx={{"&:hover": {backgroundColor: "#d12c93", }}}
                                                onClick={toggleConfirm}
                                                disabled={!enableConfirm}
                                            >
                                                {t("vehicleAvailability.status.newBlock.confirmButton")}
                                            </Mui.Button>
                                        </Mui.Box>

                                    </Mui.Stack>
                                </Mui.Grid>

                            </Mui.Grid>

                            {listBlocks.map((element, index)=> {
                                if (element.reasonCodeState === EnumReasonCodeState.CLOSED) {
                                    return null;
                                }

                                return(
                                    <VehicleAvailabilityBlock
                                        listStatusSpecial={listStatusSpecial}
                                        blockType={element.statusReasonCode}
                                        fromDate={element.actualStartUnavailabilityDatetime ? element.actualStartUnavailabilityDatetime :element.expectedStartUnavailabilityDatetime}
                                        toDate={element.expectedEndUnavailabilityDatetime}
                                        reserved={element.reservedStatusCode}
                                        restricted={element.restrictedStatusCode}
                                        manuallyRemovable={element.manuallyRemovable}
                                        onDeleteBlock={() => onRemove(index)}
                                    />
                                )
                            })}

                            {listBlocks.length === 0 && <Mui.Typography variant="body1" pt="40px">
                                {t("vehicleAvailability.status.currentState.noBlocks")}
                            </Mui.Typography>}
                        </Mui.CardContent>
                    </Mui.Card>
                </Mui.Grid>

                <Mui.Grid item md={6} xs={12}>
                    <Mui.Card>
                        <Mui.CardContent>
                            <Mui.Typography variant="h5">
                                {t("vehicleAvailability.status.newBlock.title")}
                            </Mui.Typography>
                            
                            <form onSubmit={form.handleSubmit((data) => onSumbit(data))}>
                               <Mui.Grid container spacing={2}> 
                                    <Mui.Grid item xs={12}>
                                        <AutoComplete 
                                            fullWidth
                                            options={listStatus}
                                            label={t("vehicleAvailability.status.newBlock.inputs.reasonCode")}
                                            {...form.register("status", {
                                                required: t("vehicleAvailability.status.newBlock.inputs.reasonCodeRequired")
                                            })}
                                            value={form.watch("status")}
                                            onChange={(newStatusValue) => {
                                                form.setValue("status", newStatusValue);
												form.setValue("startDate", null);
                                                form.setValue("endDate", null);

                                            }}
                                            error={form.formState.errors?.status?.message?.toString()}
                                            getOptionDisabled={(option) => option.disabled}
                                        />
                                    </Mui.Grid>

                                    <Mui.Grid item md={6} xs={12}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                renderInput={(props) => (
                                                    <Mui.TextField
                                                        {...props}
                                                        fullWidth
                                                        error={form.formState.errors.startDate ? true : false}
                                                        helperText={form.formState.errors.startDate?.message}
                                                    />
                                                )}
                                                ampm={false}
                                                inputFormat={'DD/MM/YYYY HH:mm'}
                                                label={t("vehicleAvailability.status.newBlock.inputs.startDate")}
                                                value={form.watch("startDate")}
                                                shouldDisableDate={disabledStartDates}
                                                shouldDisableTime={disabledStartHours}
                                                {...form.register("startDate", {
                                                    required: t("vehicleAvailability.status.newBlock.inputs.startDateRequired"),
                                                    validate: (value) => {
                                                        const dateSelected = dayjs(value);
                                                        if (value && (!dateSelected.isValid() || (disabledStartDates(value)
                                                            || (!disabledStartDates(value) && disabledStartHours(dateSelected.get("hour"), "hours"))
                                                            || (!disabledStartDates(value) && !disabledStartHours(dateSelected.get("hour"), "hours")
                                                                && disabledStartHours(dateSelected.get("minute"), "minutes"))))) {
                                                            return t("vehicleAvailability.status.newBlock.inputs.startDateInvalid");
                                                        }
                                                    }
                                                })}
                                                onChange={(newTodayValue) => {
                                                    form.setValue("startDate", newTodayValue);
                                                }}
                                                onViewChange={(view) => {
                                                    if (view === "hours") {
                                                        const newVal = dayjs(form.watch("startDate")).set("hour", dayjs().get("hour")).set("minute", dayjs().get("minute"));
                                                        form.setValue("startDate", newVal.toISOString());
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>    
                                    </Mui.Grid>
                                    
                                    <Mui.Grid item md={6} xs={12}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                renderInput={(props) => (
                                                    <Mui.TextField
                                                        {...props}
                                                        fullWidth
                                                        error={form.formState.errors.endDate ? true : false}
                                                        helperText={form.formState.errors.endDate?.message}
                                                        onReset={(event) => form.setValue("endDate", null)}
                                                    />
                                                )}
                                                ampm={false}
                                                inputFormat={'DD/MM/YYYY HH:mm'}
                                                label={t("vehicleAvailability.status.newBlock.inputs.endDate")}
                                                value={form.watch("endDate")}
                                                shouldDisableDate={(date) => dayjs(date).isBefore(dayjs(form.watch("startDate")).subtract(1, "day"))}
                                                shouldDisableTime={disableEndHours}
                                                {...form.register("endDate", {
                                                    validate: (value) => {
                                                        const dateSelected = dayjs(value);
                                                        if (value && !dateSelected.isValid()) {
                                                            return t("vehicleAvailability.status.newBlock.inputs.endDateInvalid");
                                                        }
                                                        if (dateSelected.isBefore(dayjs(form.watch("startDate")))) {
                                                            return t("vehicleAvailability.status.newBlock.inputs.endDateBeforeStart");
                                                        }
                                                    }
                                                })}
                                                onChange={(newDateValue) => {
                                                    form.setValue("endDate", newDateValue);
                                                }}
												disabled={form.watch("status") == 'SOLD'}

                                            />
                                            </LocalizationProvider>   
                                    </Mui.Grid>
									<Mui.Grid item md={6} xs={12}></Mui.Grid>										 
                                    <Mui.Grid item md={6} xs={12}>
                                        <Mui.Button
                                            fullWidth
                                            variant="contained"
                                            style={{ color: "white", backgroundColor: "#d12c93"}}
                                            type="submit"
                                        >
                                            {t("vehicleAvailability.status.newBlock.addButton")}
                                        </Mui.Button>
                                    </Mui.Grid>
                               </Mui.Grid>
                            </form>   
                        </Mui.CardContent>   
                    </Mui.Card>   
                </Mui.Grid>
            </Mui.Grid>
        </Mui.Box>

        <Dialogs.ConfirmBlock
            open={openConfirm}
            onClose={toggleConfirm}
            onConfirm={onConfirmBlocks}
            listStatus={listStatusSpecial}
            vehicleId={vehicleData.id}
            listBlocks={listBlocks}
            tenant={vehicleData.tenant}
        />

        <Dialogs.ChangeStatus
            open={openChangeStatus}
            onClose={toggleChangeStatus}
            onChangeStatus={onChangeAvailabilityStatus}
            vehicleId={vehicleData.id}
            currentStatus={availabilityStatus?.availabilityHistory?.availabilityCode || ""}
            tenant={vehicleData.tenant}
            vehiclePlate={vehicleData.plate}
        />
    </>
    )
}
export default VehicleAvailabilityStatus;
